// One-time link tokens (password reset, e-mail verification, team invites, magic login) — P4-F4 (STRIPE Phase 4).
//
// A token must never sit in a URL that reaches a server or a tracker: in the API path it was written by every request log
// (Cloud Run `httpRequest.requestUrl`, proxies) and stayed usable; in the page URL it reaches GA4 / GTM / the Meta Pixel /
// New Relic / Sentry and outbound referrers. So:
//   - mailed links carry it in the FRAGMENT (`/reset-password?email=…#token=…`); links mailed before (and every link while
//     the backend's MAIL_TOKEN_IN_FRAGMENT flag is off) carry it in the PATH (`/reset-password/<token>?email=…`);
//   - the inline bootstrap at the top of public/index.html reads either shape BEFORE any tracker loads, keeps the token in
//     memory (`window.__RF_LINK__`) and replaces the address with the tokenless one;
//   - the pages read it through `useLinkToken`, and the API receives it only in the request BODY.
// `parseLinkLocation` is the same rule as the bootstrap (which cannot import it); `linkToken.test.ts` runs both on the
// same URLs.

export const LINK_TOKEN_PAGES = ["/reset-password", "/email-verify", "/auth/magic", "/accept_member_invite", "/accept_invite"] as const;
export type LinkTokenPage = typeof LINK_TOKEN_PAGES[number];

export interface ICapturedLinkToken {
  path: LinkTokenPage;
  token: string;
}

declare global {
  interface Window {
    __RF_LINK__?: ICapturedLinkToken;
  }
}

// `/page`, `/page/<token>`; email-verify alone may carry the plan after the token (`/<token>/<product>/<price>`).
const LINK_PAGE = /^\/(reset-password|email-verify|auth\/magic|accept_member_invite|accept_invite)(?:\/([^/]+))?(?:\/([^/]+))?(?:\/([^/]+))?\/?$/;

const safeDecode = (segment: string) => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

export interface ILinkLocation {
  path: LinkTokenPage;
  token: string | null;
  search: string;
  hash: string;
  // The URL as given still carries a token (fragment key or path segment) and must be replaced.
  dirty: boolean;
}

export const parseLinkLocation = (pathname: string, search: string, hash: string): ILinkLocation | null => {
  const match = LINK_PAGE.exec(pathname);
  if (!match) return null;
  const [, page, segment, product, price] = match;
  if (product && page !== "email-verify") return null;

  const fragment = new URLSearchParams(hash.replace(/^#/, ""));
  const fromFragment = fragment.get("token");
  const query = new URLSearchParams(search);
  if (page === "email-verify") {
    if (product && !query.has("product_id")) query.set("product_id", safeDecode(product));
    if (price && !query.has("price_id")) query.set("price_id", safeDecode(price));
  }
  fragment.delete("token");
  const qs = query.toString();
  const rest = fragment.toString();

  return {
    path: `/${page}` as LinkTokenPage,
    // The fragment wins: it is the new mail; the path is the fallback for links mailed before.
    token: fromFragment || (segment ? safeDecode(segment) : null),
    search: qs ? `?${qs}` : "",
    hash: rest ? `#${rest}` : "",
    dirty: Boolean(segment) || fromFragment !== null,
  };
};

// The token the bootstrap captured for THIS page (memory only — never storage, history state or a URL).
export const readCapturedLinkToken = (page: LinkTokenPage): string | null => {
  const captured = typeof window !== "undefined" ? window.__RF_LINK__ : undefined;

  return captured && captured.path === page && captured.token ? captured.token : null;
};

// Backstop for every sink that records a URL (GA4 page_view, Sentry breadcrumbs / spans / events / Replay): a token in a
// legacy page path, an old API path, or a `token=` / `link_token=` / `site_preview=` parameter becomes a placeholder.
const PAGE_TOKEN = /\/(reset-password|email-verify|auth\/magic|accept_member_invite|accept_invite)\/[^/?#\s"']+/g;
const API_TOKEN = /\/(users\/reset_password|users\/verify_email|teams\/accept_member_invite|teams\/accept_invite)\/[^/?#\s"']+/g;
const PARAM_TOKEN = /([?#&])(token|link_token|site_preview)=[^&#\s"']*/g;

// A request body serialized into a log line or breadcrumb: `"token":"…"`, also when escaped once more (`\"token\":\"…\"`).
const BODY_SECRET = /("(?:token|link_token|password|password_replay)"\s*:\s*")[^"]*(")/g;
const ESCAPED_BODY_SECRET = /(\\"(?:token|link_token|password|password_replay)\\"\s*:\s*\\")(?:[^"\\]|\\[^"])*(\\")/g;

export const redactLinkTokens = (value: string): string =>
  value
    .replace(PAGE_TOKEN, "/$1/:token")
    .replace(API_TOKEN, "/$1/:token")
    .replace(PARAM_TOKEN, "$1$2=[Filtered]")
    .replace(ESCAPED_BODY_SECRET, "$1[Filtered]$2")
    .replace(BODY_SECRET, "$1[Filtered]$2");

// Object keys whose string value is a credential wherever it appears in telemetry (a logged request body, breadcrumb data).
export const SECRET_FIELD_NAMES = new Set(["token", "link_token", "password", "password_replay"]);
